import { useState, useCallback, useMemo } from 'react';

const STORAGE_KEY = 'squareup_rows_per_page';
const DEFAULT_ROWS_PER_PAGE = 10;
const DEFAULT_OPTIONS = [10, 25, 50];

/**
 * Get the stored rows per page value from localStorage
 * Useful for server-side paginated components that need the initial value
 */
export function getStoredRowsPerPage(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const value = parseInt(stored, 10);
      if (!isNaN(value) && value > 0) {
        return value;
      }
    }
  } catch {
    // localStorage not available
  }
  return DEFAULT_ROWS_PER_PAGE;
}

/**
 * Set the stored rows per page value in localStorage
 * Call this when changing pageSize in server-side paginated components
 */
export function setStoredRowsPerPage(value: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // localStorage not available
  }
}

export interface UsePaginationOptions {
  defaultRowsPerPage?: number;
  rowsPerPageOptions?: number[];
}

export interface UsePaginationReturn {
  page: number;
  rowsPerPage: number;
  rowsPerPageOptions: number[];
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  handlePageChange: (event: unknown, newPage: number) => void;
  handleRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  resetPage: () => void;
  getPaginatedItems: <T>(items: T[]) => T[];
  paginationProps: {
    page: number;
    rowsPerPage: number;
    rowsPerPageOptions: number[];
    onPageChange: (event: unknown, newPage: number) => void;
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  };
}

/**
 * Reusable pagination hook with persistent rowsPerPage across the site
 *
 * @example
 * const { page, rowsPerPage, paginationProps, getPaginatedItems, resetPage } = usePagination();
 *
 * // Use with MUI TablePagination
 * <TablePagination
 *   count={items.length}
 *   {...paginationProps}
 * />
 *
 * // Get paginated items
 * const paginatedItems = getPaginatedItems(filteredItems);
 */
export function usePagination(options: UsePaginationOptions = {}): UsePaginationReturn {
  const {
    defaultRowsPerPage = getStoredRowsPerPage(),
    rowsPerPageOptions = DEFAULT_OPTIONS,
  } = options;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPageState] = useState(() => {
    // Ensure the stored value is one of the options, otherwise use the closest option
    const stored = getStoredRowsPerPage();
    if (rowsPerPageOptions.includes(stored)) {
      return stored;
    }
    // Find closest option
    const closest = rowsPerPageOptions.reduce((prev, curr) =>
      Math.abs(curr - stored) < Math.abs(prev - stored) ? curr : prev
    );
    return closest || defaultRowsPerPage;
  });

  const setRowsPerPage = useCallback((value: number) => {
    setRowsPerPageState(value);
    setStoredRowsPerPage(value);
    setPage(0); // Reset to first page when changing rows per page
  }, []);

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
  }, [setRowsPerPage]);

  const resetPage = useCallback(() => {
    setPage(0);
  }, []);

  const getPaginatedItems = useCallback(<T,>(items: T[]): T[] => {
    const start = page * rowsPerPage;
    return items.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage]);

  const paginationProps = useMemo(() => ({
    page,
    rowsPerPage,
    rowsPerPageOptions,
    onPageChange: handlePageChange,
    onRowsPerPageChange: handleRowsPerPageChange,
  }), [page, rowsPerPage, rowsPerPageOptions, handlePageChange, handleRowsPerPageChange]);

  return {
    page,
    rowsPerPage,
    rowsPerPageOptions,
    setPage,
    setRowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    resetPage,
    getPaginatedItems,
    paginationProps,
  };
}
